import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  IconButton,
  Stack,
} from '@mui/material';
import api from '../../services/api';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import {
  DataGridPro, GridColDef, GridFilterModel,
  GridFooter, GridPaginationModel, GridSortModel,
} from '@mui/x-data-grid-pro';
import {datagridColors} from '../../assets/datagridStyles';
import {useGridApiRef} from '@mui/x-data-grid';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import {Header} from "../AdminPages/Header";
import ReactDOM from "react-dom";
import SelectAddressField from "../ServiceProviderPages/components/fields/SelectAddressField";
import {toISODateString} from "../../services/toISODateString";
import {useStateContext} from "../../hooks/useStateContext";

const autosizeOptions = {
  includeHeaders: true,
  includeOutliers: true,
  outliersFactor: 1.5,
  expand: true,
};

export const ProviderConfirmations: ComponentType = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const apiRef = useGridApiRef();
  const [customFields, setCustomFields] = useState<any>([]);
  const { viewPeriod } = useStateContext();

  useEffect(() => {
    document.title = `Zoe - Provider Confirmations`;
  }, []);

  const searchParams = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (viewPeriod[0]) {
      searchParams.append('from', toISODateString(viewPeriod[0]));
    }
    if (viewPeriod[1]) {
      searchParams.append('to', toISODateString(viewPeriod[1]));
    }
    return searchParams;
  }, [viewPeriod]);

  const fetchRows = useCallback(() => {
    setLoading(true);

    api.get(`/service-reservation-confirmations/all`, searchParams).then((res: any) => {

      setLoading(false);
      setCustomFields(res.custom_fields);

      ReactDOM.flushSync(() => {
        setRows(res.data);
        if (apiRef?.current) {
          apiRef.current.updateRows(res.data);
        }
      });

    })
      .then(() => sleep(0))
      .then(() => {
        if (apiRef?.current) {
          apiRef.current.autosizeColumns(autosizeOptions);
        }
      });
  }, [selectedOption,searchParams]);

  useEffect(() => {
    fetchRows();
  }, [selectedOption, searchParams]);

  const [filterModelStatic, setFilterModelStatic] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModelStatic, setSortModelStatic] = useState<GridSortModel>([]);
  const [paginationModelStatic, setPaginationModelStatic] = useState<
    GridPaginationModel
  >({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef[] = useMemo(() => {
    const columnArray: GridColDef[] = [
      {
        field: 'serial_number',
        type: 'string',
        headerName: '',
        cellClassName: '',
        editable: false,
        width: 50,
        maxWidth: 50,
        renderCell: (params: any) => {
          const currentPage = paginationModelStatic.page;
          const pageSize = paginationModelStatic.pageSize;

          const rowIndex =
            currentPage * pageSize +
            params.api.getRowIndexRelativeToVisibleRows(params.id) +
            1;

          const id = params.id;
          return (
            <>
              {rowIndex}
            </>
          );
        },
        renderHeaderFilter: () => {
          return <></>;
        },
      },
      {
        field: 'service_provider',
        headerName: 'Provider',
        editable: false,
        type: 'string',
        minWidth: 125,
        valueGetter: (params) =>
          params.value?.name,
      },
      {
        field: 'service',
        headerName: 'Szolgáltatás',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'activity',
        headerName: 'Program',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'group',
        headerName: 'Csoport',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'size',
        headerName: 'Létszám',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'date',
        headerName: 'Dátum',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'pickup_time',
        headerName: 'Pickup',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'start_time',
        headerName: 'Kezdés',
        renderCell: (props: any) => {
          const isStaff = props?.row?.formatted_type === 'event-staff-airport-transfer';
          const value = isStaff ? props?.row?.pickup_time : props?.value;
          return <p dangerouslySetInnerHTML={{ __html: value }} />
        }
      },
      {
        field: 'arrival_time',
        headerName: 'Érkezés',
        renderCell: (props: any) => {
          const isStaff = props?.row?.formatted_type === 'event-staff-airport-transfer';
          const value = isStaff ? props?.row?.pickup_arrival_time : props?.value;
          return <p dangerouslySetInnerHTML={{ __html: value }} />
        }
      },
      {
        field: 'flight',
        headerName: 'Járatszám',
        renderCell: (props: any) => {
          const isTransfer = props?.row?.formatted_type === 'airport-transfer';
          return <p dangerouslySetInnerHTML={{ __html: isTransfer ? props?.value : '' }} />
        }
      },
      {
        field: 'meeting_place',
        headerName: 'Találkozási pont',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'event_staff',
        headerName: 'Fellépő',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'current_group_size',
        headerName: 'Aktuális létszám',
        renderCell: (props: any) => <p dangerouslySetInnerHTML={{ __html: props?.value }} />
      },
      {
        field: 'driver',
        headerName: 'Sofőr',
        renderCell: (props: any) => {
          const value = props?.row?.driver?.name || props?.row?.driver || '';
          return <p>{value}</p>;
        },
      },
      {
        field: 'vehicle',
        headerName: 'Jármű',
        renderCell: (props: any) => {
          const value = props?.row?.vehicle?.name || props?.row?.vehicle || '';
          return <p>{value}</p>;
        },
      },
      {
        field: 'venue',
        headerName: 'Helyszín',
        editable: false,
        type: 'text',
        renderCell: (props: any) => {

          const defaultValue = props?.row.formatted_type === 'event-staff-airport-transfer' ? props?.row?.meeting_place : props?.row?.venue;

          let name = '';
          if (defaultValue?.name) {
            name = defaultValue?.name;
            if (defaultValue?.address_line1 && defaultValue?.address_line1 != defaultValue?.name) {
              name = `${name} (${defaultValue?.address_line1})`;
            }
          } else {
            name = defaultValue;
          }

          return <p
            dangerouslySetInnerHTML={{__html: name}}></p>;
        },
        renderEditCell: (props: any) => {
          return <SelectAddressField params={props}/>;
        }
      },
      {
        field: 'guide',
        headerName: 'Guide',
        editable: false,
        type: 'text',
        renderCell: (props: any) => {
          return <p
            dangerouslySetInnerHTML={{__html: props?.value}}></p>
        }
      }
    ];
    customFields.map((customField: any) => {
      if (customField?.type === 'string') {
        columnArray.push({
          field: customField?.column_name,
          headerName: customField?.name,
          editable: false,
          type: 'text',
          renderCell: (props: any) => {
            return props?.value;
          }
        });
      }
      if (customField?.type === 'boolean') {
        columnArray.push({
          field: customField?.column_name,
          headerName: customField?.name,
          editable: false,
          type: 'boolean',
        });

      }
      if (customField?.type === 'driver') {
        columnArray.push({
          field: customField?.column_name,
          headerName: customField?.name,
          editable: false,
          type: 'string',
          renderCell: (props: any) => {
            return <div>{props?.value?.name}</div>;
          }
        });
      }
      if (customField?.type === 'float') {
        columnArray.push({
          field: customField?.column_name,
          headerName: customField?.name,
          editable: false,
          type: 'number',
        });
      }
      if (customField?.type === 'text') {
        columnArray.push({
          field: customField?.column_name,
          headerName: customField?.name,
          editable: false,
          type: 'string',
          renderCell: (props: any) => {
            return <div dangerouslySetInnerHTML={{__html: props?.value}}></div>;
          }
        });
      }
    });
    return columnArray;
  }, [
    rows,
    sortModelStatic,
    filterModelStatic,
    paginationModelStatic,
    apiRef,
    customFields
  ]);
  const CustomFooter = () => {
    return (
      <Stack
        direction={'row'}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px',
        }}
      >
        <IconButton
          sx={{
            visibility: 'hidden'
          }}
          onClick={() => {

          }}
        >
          <AddIcon color={'primary'}/>
        </IconButton>

        <GridFooter/>
      </Stack>
    );
  };
  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }
  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }
  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  const handleFilterModelChange = useCallback((newModel: any) => {
    setFilterModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel)
        ? prevModel
        : newModel
    );
  }, []);

  const handleSortModelChange = useCallback((newModel: any) => {
    setSortModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel)
        ? prevModel
        : newModel
    );
  }, []);

  const handlePaginationModelChange = useCallback((newModel: any) => {
    setPaginationModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel)
        ? prevModel
        : newModel
    );
  }, []);

  return (
    <>
      <Header
        title={'Provider Confirmations'}
        rowCount={rows.length}
        hasPagination={false}
        apiRef={apiRef}
        showMenu={true}
        tableData={{
          columns: columns,
          rows: rows,
        }}
        showPeriodSelector={true}
        refreshTableData={()=>{
          fetchRows();
        }}
      />

      <Box sx={datagridColors('calc(100vh - 145px)')}>
        <DataGridPro
          apiRef={apiRef}
          sx={{
            pointerEvents: loading ? 'none' : 'auto',
            opacity: loading ? 0.5 : 1,
            maxHeight: 'calc(100% - 70px)',
          }}
          rows={rows}
          getRowClassName={(params: any) => {
            const color =
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
            return `${color}`;
          }}
          disableColumnMenu={true}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          columnHeaderHeight={38}
          rowHeight={38}
          pagination
          slots={{
            // @ts-ignore
            columnSortedDescendingIcon: CustomSortIconDesc,
            columnSortedAscendingIcon: CustomSortIconAsc,
            columnUnsortedIcon: CustomSortIcon,
            footer: CustomFooter,
            headerFilterMenu: null,
          }}
          unstable_headerFilters={false}
          pageSizeOptions={[25, 50, 100, {label: 'All', value: rows.length}]}
          initialState={{
            pinnedColumns: { left: ['serial_number'], right: ['actions'] },
          }}
          filterMode={'client'}
          disableChildrenFiltering={true}
          onFilterModelChange={handleFilterModelChange}
          sortingMode={'client'}
          onSortModelChange={handleSortModelChange}
          disableMultipleColumnsSorting={false}
          onPaginationModelChange={handlePaginationModelChange}
        />
      </Box>

    </>
  );
};

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
